"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import { cn } from "@/lib";
import { ReactNode, useState } from "react";
import { Path, UseFormReturn } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "..";
type Option = {
  value: string | number;
  label: string;
};
type CustomSelectProps<TFormValues extends Record<string, any>> = {
  form: UseFormReturn<TFormValues>;
  name: Path<TFormValues>;
  label?: string;
  description?: ReactNode;
  options: Option[];
  placeholder?: string;
  helpText?: string;
  onChange?: (value: string) => void;
  required?: boolean;
  className?: string;
  showSearch?: boolean;
};
export type FormSelectProps<TFormValues extends Record<string, any>> =
  CustomSelectProps<TFormValues>;
export function FormSelect<TFormValues extends Record<string, any>>({
  form,
  name,
  label,
  description,
  options,
  required = false,
  placeholder,
  helpText,
  onChange,
  className,
  showSearch = false,
  ...props
}: FormSelectProps<TFormValues>) {
  const [searchQuery, setSearchQuery] = useState("");
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  return (
    <div className={className}>
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => {
          return (
            <FormItem>
              {label ? (
                <FormLabel className="tw:gap-0">
                  {label}
                  {required ? (
                    <span className="tw:text-destructive">*</span>
                  ) : (
                    ""
                  )}
                </FormLabel>
              ) : null}
              <Select
                onValueChange={(value) => {
                  onChange?.(value);
                  field.onChange(value);
                }}
                value={field.value?.toString() || ""}
                {...props}
              >
                <FormControl>
                  <SelectTrigger
                    className={cn(
                      "tw:!w-full",
                      !field.value && "tw:[&>span]:!text-[#A7A7A7]",
                    )}
                  >
                    <SelectValue placeholder={placeholder} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="tw:w-[var(--radix-popper-anchor-width)]">
                  {showSearch && (
                    <SearchInput
                      value={searchQuery}
                      onChange={setSearchQuery}
                    />
                  )}
                  <div className="tw:max-h-[200px] tw:overflow-y-auto">
                    {filteredOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={String(option.value)}
                        className="tw:flex tw:items-center tw:space-x-3 tw:space-y-0"
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                    {filteredOptions.length === 0 && (
                      <div className="tw:px-2 tw:py-2 tw:text-sm tw:text-gray-500">
                        No results found
                      </div>
                    )}
                  </div>
                </SelectContent>
              </Select>
              {description && <FormDescription>{description}</FormDescription>}
              <FormMessage />
            </FormItem>
          );
        }}
      />
    </div>
  );
}
interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
}
export function SearchInput({ value, onChange }: SearchInputProps) {
  return (
    <div className="tw:px-0.5 tw:pb-2">
      <Input
        placeholder="Search..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="tw:h-8"
        autoFocus
        // Stop propagation to prevent the select from closing
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      />
    </div>
  );
}
